import * as signalR from "@aspnet/signalr";
import { ssmTokenService } from "./token-service";
import { webPushClientService } from "./WebPushClientService";

export type CitaEventoTipo =
  | "cita_creada"
  | "cita_reprogramada"
  | "cita_cancelada"
  | "cita_estado_cambiado";

export interface CitaEvento {
  tipo: CitaEventoTipo;
  payload: any;
}

type CitaEventoHandler = (evento: CitaEvento) => void;

class CitasHubService {
  private connection: signalR.HubConnection | null = null;
  private intentionalStop = false;
  private reconnectAttempts = 0;
  private handlers: CitaEventoHandler[] = [];

  /** ConnectionId activo. Envíalo como header X-SignalR-ConnectionId en las peticiones HTTP. */
  public connectionId: string | null = null;

  startConnection() {
    this.intentionalStop = false;

    if (
      this.connection &&
      this.connection.state === signalR.HubConnectionState.Connected
    )
      return;

    const rootPath =
      (process.env.VUE_APP_ROOT_PATH as string) || "http://localhost:5000/";
    const baseUrl = rootPath.replace(/\/api\/$/, "").replace(/\/$/, "");
    const token = ssmTokenService.getToken();

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${baseUrl}/citasHub`, {
        accessTokenFactory: () => token || "",
        transport:
          signalR.HttpTransportType.WebSockets |
          signalR.HttpTransportType.ServerSentEvents |
          signalR.HttpTransportType.LongPolling,
      })
      .build();

    this.connection.on("cita_evento", (evento: CitaEvento) => {
      this.handlers.forEach((h) => h(evento));
      this._mostrarNotificacion(evento);
    });

    this.connection.onclose(() => {
      this.connectionId = null;
      if (!this.intentionalStop) this._scheduleReconnect();
    });

    this.connection
      .start()
      .then(() => {
        this.connectionId = (this.connection as any).connectionId || null;
        this.reconnectAttempts = 0;
        console.log("[CitasHub] Conectado. ConnectionId:", this.connectionId);
        // Suscripción Web Push (notificaciones aunque la pestaña esté cerrada)
        webPushClientService.suscribirSiPermitido().catch(() => {});
      })
      .catch((err: any) => {
        console.error("[CitasHub] Error al conectar:", err.message || err);
        if (!this.intentionalStop) this._scheduleReconnect();
      });
  }

  stopConnection() {
    this.intentionalStop = true;
    this.connectionId = null;
    if (this.connection) { this.connection.stop().catch(() => {}); }
  }

  reconnect() {
    this.intentionalStop = true;
    if (this.connection) {
      this.connection.stop().then(() => {
        this.intentionalStop = false;
        this.startConnection();
      });
    }
  }

  /** Registra un handler que se llama con cada evento recibido. */
  onEvento(handler: CitaEventoHandler): () => void {
    this.handlers.push(handler);
    return () => {
      this.handlers = this.handlers.filter((h) => h !== handler);
    };
  }

  /** Devuelve el header que deben incluir las peticiones HTTP para excluirse de la notificación. */
  getConnectionHeader(): Record<string, string> {
    if (!this.connectionId) return {};
    return { "X-SignalR-ConnectionId": this.connectionId };
  }

  private _mostrarNotificacion(evento: CitaEvento) {
    if (!("Notification" in window)) return;
    if (Notification.permission !== "granted") return;

    const { titulo, cuerpo } = this._textoEvento(evento);

    // Usar el service worker si está disponible (notificación persistente)
    if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.ready.then((reg) => {
        reg.showNotification(titulo, {
          body: cuerpo,
          icon: "/logo_cliente.png",
          badge: "/logo_cliente.png",
          tag: `cita-${evento.tipo}`,
          data: evento.payload,
        } as any);
      });
    } else {
      new Notification(titulo, {
        body: cuerpo,
        icon: "/logo_cliente.png",
      });
    }
  }

  private _textoEvento(evento: CitaEvento): { titulo: string; cuerpo: string } {
    const p = evento.payload || {};

    switch (evento.tipo) {
      case "cita_creada":
        return {
          titulo: "Nueva cita solicitada",
          cuerpo: [
            p.nombre && `Paciente: ${p.nombre}`,
            p.fecha &&
              `Fecha: ${this._formatFecha(new Date(p.fecha))}`,
          ]
            .filter(Boolean)
            .join(" · "),
        };

      case "cita_reprogramada":
        return {
          titulo: "Cita reprogramada",
          cuerpo: p.nueva_fecha
            ? `Nueva fecha: ${this._formatFecha(new Date(p.nueva_fecha))}`
            : "Un paciente ha cambiado su cita de fecha.",
        };

      case "cita_cancelada":
        return {
          titulo: "Cita cancelada",
          cuerpo: "Un paciente ha cancelado su cita.",
        };

      case "cita_estado_cambiado":
        return {
          titulo: "Estado de cita actualizado",
          cuerpo: p.estado_nuevo
            ? `Nuevo estado: ${p.estado_nuevo}`
            : p.accion
            ? `Acción: ${p.accion}`
            : "Se ha actualizado el estado de una cita.",
        };

      default:
        return { titulo: "Notificación de citas", cuerpo: "" };
    }
  }

  private _formatFecha(d: Date): string {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  }

  private _scheduleReconnect() {
    const delay = Math.min(2000 * Math.pow(2, this.reconnectAttempts), 30000);
    this.reconnectAttempts++;
    setTimeout(() => {
      if (!this.intentionalStop) this.startConnection();
    }, delay);
  }
}

export const citasHubService = new CitasHubService();
